import { Locale } from '@ivario-arc/shared/src/types/locales';

const LANG_STORAGE_KEY = 'ivario_lang';
const LANG_PENDING_SYNC_KEY = 'ivario_lang_pending_sync';
export const SUPPORTED_LOCALES: readonly Locale[] = [
  Locale.DE,
  Locale.EN,
  Locale.NL,
];

export function toSupportedLocale(
  value: string | null | undefined,
): Locale | null {
  if (!value) return null;
  const normalized = value.toLowerCase().split('-')[0];
  const match = SUPPORTED_LOCALES.find((l: string) => l === normalized);
  return match ?? null;
}

export function getStoredLanguage(): Locale | null {
  try {
    return toSupportedLocale(localStorage.getItem(LANG_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function setStoredLanguage(locale: Locale): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, locale);
  } catch {
    // localStorage unavailable
  }
}

export function clearStoredLanguage(): void {
  try {
    localStorage.removeItem(LANG_STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

// Marks a language as freshly chosen on the landing page so the next Callback
// pushes it to Keycloak. Without this flag we cannot distinguish a fresh
// landing-page choice from a stale localStorage value carried over from a
// previous session, and we would otherwise overwrite the user's Keycloak
// settings change with the old localStorage value.
export function markLanguagePendingSync(locale: Locale): void {
  try {
    localStorage.setItem(LANG_PENDING_SYNC_KEY, locale);
  } catch {
    // localStorage unavailable
  }
}

export function consumePendingLanguageSync(): Locale | null {
  try {
    const value = localStorage.getItem(LANG_PENDING_SYNC_KEY);
    if (value !== null) localStorage.removeItem(LANG_PENDING_SYNC_KEY);
    return toSupportedLocale(value);
  } catch {
    return null;
  }
}

export type LanguageSource = 'url' | 'storage' | 'browser';

export function resolveLanguage(
  searchParams: URLSearchParams,
  i18nResolvedLanguage: string | undefined,
): { locale: Locale; source: LanguageSource } {
  // 1st priority: URL parameter ?lang=
  const urlLang = toSupportedLocale(searchParams.get('lang'));
  if (urlLang) {
    return { locale: urlLang, source: 'url' };
  }

  // 2nd priority: localStorage
  const storedLang = getStoredLanguage();
  if (storedLang) {
    return { locale: storedLang, source: 'storage' };
  }

  // 3rd priority: browser language (via i18next-browser-languagedetector)
  const browserLang = toSupportedLocale(i18nResolvedLanguage);
  return { locale: browserLang ?? Locale.DE, source: 'browser' };
}
